import {
  IonButtons,
  IonModal,
  IonTitle,
  IonToolbar,
  IonHeader,
  IonContent,
  IonIcon,
  IonText,
  IonSpinner,
  IonItem,
  IonLabel,
} from '@ionic/react';
import type { ReactElement } from 'react';
import DynamicButton from 'components/buttons/DynamicButton';
import {
  ComponentProps,
  FunctionComponent,
  useRef,
  useState,
  useEffect,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { ExploreElement } from '../../models/ExploreElementModel/ExploreElementModel';
import { RunModel } from '../../models/RunModel/RunModel';
import { useStores } from '../../models';
import { useCloudStorage } from '../../services/Database/CloudStorageService';
import { useFSM } from '../../services/DongleController/DongleController';

export type DummyRecordInfo = {
  totalTime: number;
  bleConnected: number;
  bleDisconnected: number;
  sampleCount: number;
};

export interface RecordInfoModalProps extends ComponentProps<typeof IonModal> {
  isOpen: boolean;
  onDidDismiss: () => void;
  recordInfo?: ExploreElement | DummyRecordInfo | null;
  score?: number;
  onRunClick?: (run: RunModel) => void; // NEU
}

const RecordInfoModal: FunctionComponent<RecordInfoModalProps> = ({
  isOpen,
  onDidDismiss,
  recordInfo,
  ...rest
}) => {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLIonModalElement>(null);
  const fsm = useFSM();
  // Spinner: Pollt im Intervall, solange das Modal offen ist, um sofort auf Änderungen zu reagieren
  // Spinner: Zeige nur, wenn es eine offene Session gibt, die noch nicht abgeschlossen ist UND in dieser Session noch keine Samples angekommen sind
  const [isWaitingForSamples, setIsWaitingForSamples] = useState(() => {
    if (!Array.isArray(fsm.recordSessionTimestamps)) return false;
    // Finde die letzte Session
    const lastIdx = fsm.recordSessionTimestamps.length - 1;
    if (lastIdx < 0) return false;
    const [start, end] = fsm.recordSessionTimestamps[lastIdx];
    // Session ist offen, wenn end === null
    if (end !== null) return false;
    // Prüfe, ob in der letzten Session überhaupt Samples angekommen sind
    const sessionSamples =
      Array.isArray(fsm.recordSessions) && fsm.recordSessions[lastIdx]
        ? fsm.recordSessions[lastIdx].length
        : 0;
    // Zeige Spinner nur, wenn die letzte Session offen ist und noch keine Samples hat
    return sessionSamples === 0;
  });

  useEffect(() => {
    if (!isOpen) return;
    const interval = setInterval(() => {
      if (!Array.isArray(fsm.recordSessionTimestamps)) {
        setIsWaitingForSamples(false);
        return;
      }
      const lastIdx = fsm.recordSessionTimestamps.length - 1;
      if (lastIdx < 0) {
        setIsWaitingForSamples(false);
        return;
      }
      const [start, end] = fsm.recordSessionTimestamps[lastIdx];
      if (end !== null) {
        setIsWaitingForSamples(false);
        return;
      }
      const sessionSamples =
        Array.isArray(fsm.recordSessions) && fsm.recordSessions[lastIdx]
          ? fsm.recordSessions[lastIdx].length
          : 0;
      setIsWaitingForSamples(sessionSamples === 0);
    }, 250);
    return () => clearInterval(interval);
  }, [isOpen, fsm]);

  // Debug-Ausgabe aller relevanten FSM-Werte inkl. BLE-Timesteps und Session-Timestamps (in Sekunden)
  useEffect(() => {
    if (isOpen) {
      // Defensive Kopien, um MobX-Proxies zu vermeiden
      const bleTimesteps = Array.isArray(fsm.bleTimesteps)
        ? fsm.bleTimesteps.map((x) => (Array.isArray(x) ? [...x] : x))
        : [];
      const recordSessionTimestamps = Array.isArray(fsm.recordSessionTimestamps)
        ? fsm.recordSessionTimestamps.map((x) =>
            Array.isArray(x) ? [...x] : x
          )
        : [];
      const recordSessionTimestampsResolved = Array.isArray(
        fsm.recordSessionTimestampsResolved
      )
        ? fsm.recordSessionTimestampsResolved.map((x) =>
            Array.isArray(x) ? [...x] : x
          )
        : [];
      // Hilfsfunktion: Array der Zeitdifferenzen (Sekunden, 3 Nachkommastellen)
      const diffSecArr = (arr: any) =>
        Array.isArray(arr)
          ? arr
              .map(([s, e]) =>
                typeof s === 'number' && typeof e === 'number'
                  ? ((e - s) / 1000).toFixed(3)
                  : null
              )
              .filter((x) => x !== null)
          : [];
      // Für bleTimesteps: drittes Element (connected) ignorieren
      const diffSecArrBle = (arr: any) =>
        Array.isArray(arr)
          ? arr
              .map(([s, e]) =>
                typeof s === 'number' && typeof e === 'number'
                  ? ((e - s) / 1000).toFixed(3)
                  : null
              )
              .filter((x) => x !== null)
          : [];
      console.log('[RecordInfoModal] FSM-Analytics:', {
        bleConnectedLive: fsm.bleConnectedLive,
        bleDisconnectedLive: fsm.bleDisconnectedLive,
        totalTimeLive: fsm.totalTimeLive,
        totalRecordingTime: fsm.totalRecordingTime,
        recordSamples: fsm.recordSamples.length,
        recordSessionTimestamps,
        recordSessionTimestamps_diff_sec: diffSecArr(recordSessionTimestamps),
        recordSessionTimestampsResolved,
        recordSessionTimestampsResolved_diff_sec: diffSecArr(
          recordSessionTimestampsResolved
        ),
        bleTimesteps,
        bleTimesteps_diff_sec: diffSecArrBle(bleTimesteps),
        connectionLostCounterValue: fsm.connectionLostCounterValue,
        bleConnected: fsm.bleConnected,
        bleDisconnected: fsm.bleDisconnected,
        totalTime: fsm.totalTime,
      });
    }
  }, [
    isOpen,
    fsm.bleConnectedLive,
    fsm.bleDisconnectedLive,
    fsm.totalTimeLive,
    fsm.totalRecordingTime,
    fsm.recordSamples.length,
    fsm.recordSessionTimestamps,
    fsm.recordSessionTimestampsResolved,
    fsm.bleTimesteps,
    fsm.connectionLostCounterValue,
    fsm.bleConnected,
    fsm.bleDisconnected,
    fsm.totalTime,
  ]);

  const handleClose = () => {
    onDidDismiss?.();
    modalRef.current?.dismiss();
  };

  return (
    <IonModal
      ref={modalRef}
      isOpen={isOpen}
      onDidDismiss={onDidDismiss}
      className="edit-modal"
      {...rest}
    >
      <IonHeader className="ion-no-border">
        <IonToolbar className="edit-modal-toptoolbar">
          <IonTitle>
            <Trans i18nKey="RECORD_INFO">RecordInfo Details</Trans>
          </IonTitle>
          <IonButtons slot="end">
            <DynamicButton
              text={<Trans i18nKey="CANCEL" />}
              iconName="CancelUpIcon"
              buttonType="icon"
              size="xlarge"
              fill="clear"
              className="spnx-button"
              onClick={handleClose}
            />
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent
        className="ion-padding"
        style={{ '--background': 'transparent', '--border-colour': 0 }}
      >
        {isWaitingForSamples && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              background: 'rgba(0,0,0,0.0)',
              zIndex: 2000,
            }}
          >
            <IonSpinner
              name="crescent"
              style={{
                width: 64,
                height: 64,
                color: '#fff',
                '--color': '#fff',
              }}
            />
          </div>
        )}
        <div
          style={{
            background: 'transparent',
            borderRadius: 12,
            boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            padding: 0,
            marginBottom: 24,
            overflow: 'hidden',
            opacity: isWaitingForSamples ? 0 : 1,
            pointerEvents: isWaitingForSamples ? 'none' : undefined,
            transition: 'opacity 0.2s',
          }}
        >
          {/* Gesamtdauer der Aufnahme inkl. Disconnected (FSMService.totalTime) */}
          <IonItem
            lines="none"
            style={{ background: 'transparent', fontWeight: 700, fontSize: 16 }}
          >
            <IonLabel>
              <Trans i18nKey="RECORD_TOTAL_TIME">
                Total Time (all intervals)
              </Trans>
            </IonLabel>
            <IonLabel slot="end">
              {(() => {
                const value =
                  typeof fsm.totalTime === 'number' ? fsm.totalTime : 0;
                if (!value || value < 1000) {
                  if (fsm.recordSamples.length > 0) return '0.0 s';
                  return 'unknown';
                }
                return (value / 1000).toFixed(2) + ' s';
              })()}
            </IonLabel>
          </IonItem>
          <IonItem lines="none" style={{ background: 'transparent' }}>
            <IonLabel>
              <Trans i18nKey="TOTAL_RECORD_TIME">Total Record Time</Trans>
            </IonLabel>
            <IonLabel slot="end">
              {(() => {
                // Gesamte Zeit = Summe aller verbundenen und getrennten Intervalle (Sessions + Gaps)
                const arr = Array.isArray(fsm.recordSessionTimestamps)
                  ? fsm.recordSessionTimestamps
                  : [];
                if (arr.length === 0) return 'unknown';
                // Nur abgeschlossene Sessions
                const valid = arr.filter(
                  ([start, end]) =>
                    typeof start === 'number' &&
                    typeof end === 'number' &&
                    end > start
                );
                if (valid.length === 0) return 'unknown';
                const connected = valid.reduce((acc, [start, end]) => {
                  if (typeof start === 'number' && typeof end === 'number') {
                    return acc + (end - start);
                  }
                  return acc;
                }, 0);
                let disconnected = 0;
                for (let i = 1; i < valid.length; i++) {
                  const prevEnd = valid[i - 1][1];
                  const currStart = valid[i][0];
                  if (
                    typeof prevEnd === 'number' &&
                    typeof currStart === 'number' &&
                    currStart > prevEnd
                  ) {
                    disconnected += currStart - prevEnd;
                  }
                }
                const total = connected + disconnected;
                if (!total || total < 1000) {
                  if (fsm.recordSamples.length > 0) return '0.0 s';
                  return 'unknown';
                }
                return (total / 1000).toFixed(2) + ' s';
              })()}
            </IonLabel>
          </IonItem>
          {/* Zeit, in der tatsächlich Samples empfangen wurden (Summe aller Session-Dauern) */}
          <IonItem lines="none" style={{ background: 'transparent' }}>
            <IonLabel>
              <Trans i18nKey="RECORD_SAMPLE_TIME">
                Sample Reception Time (sum)
              </Trans>
            </IonLabel>
            <IonLabel slot="end">
              {(() => {
                // Berechne die Zeitspanne zwischen erstem und letztem Sample (t_ms)
                const samples = Array.isArray(fsm.recordSamples)
                  ? fsm.recordSamples
                  : [];
                if (samples.length < 2)
                  return samples.length > 0 ? '0.0 s' : 'unknown';
                // Finde min und max t_ms
                const times = samples
                  .map((s) => (typeof s.t_ms === 'number' ? s.t_ms : null))
                  .filter((t) => typeof t === 'number') as number[];
                if (times.length < 2)
                  return times.length > 0 ? '0.0 s' : 'unknown';
                const min = Math.min(...times);
                const max = Math.max(...times);
                const duration = max - min;
                if (duration < 1) return '0.0 s';
                return (duration / 1000).toFixed(2) + ' s';
              })()}
            </IonLabel>
          </IonItem>
          {/* Array der Session-Zeitpunkte anzeigen */}
          <IonItem lines="none" style={{ background: 'transparent' }}>
            <IonLabel>
              <Trans i18nKey="SESSION_TIMESTAMPS">Session Timestamps</Trans>
            </IonLabel>
            <IonLabel
              slot="end"
              style={{ whiteSpace: 'pre-wrap', maxWidth: 220, fontSize: 11 }}
            >
              {(() => {
                const arr = Array.isArray(fsm.recordSessionTimestampsResolved)
                  ? fsm.recordSessionTimestampsResolved
                  : [];
                if (arr.length === 0) return '[]';
                const base = typeof arr[0][0] === 'number' ? arr[0][0] : 0;
                // Relativ zu base, Ausgabe in Sekunden mit 2 Nachkommastellen
                const rel = arr.map(([start, end]) => [
                  typeof start === 'number'
                    ? ((start - base) / 1000).toFixed(2)
                    : null,
                  typeof end === 'number'
                    ? ((end - base) / 1000).toFixed(2)
                    : null,
                ]);
                return JSON.stringify(rel);
              })()}
            </IonLabel>
          </IonItem>
          <IonItem lines="none" style={{ background: 'transparent' }}>
            <IonLabel>
              <Trans i18nKey="BLE_CONNECTED">BLE Connected</Trans>
            </IonLabel>
            <IonLabel slot="end">
              {(() => {
                // Nur abgeschlossene Sessions
                const arr = Array.isArray(fsm.recordSessionTimestamps)
                  ? fsm.recordSessionTimestamps
                  : [];
                if (arr.length === 0) return 'unknown';
                const total = arr.reduce((acc, [start, end]) => {
                  if (
                    typeof start === 'number' &&
                    typeof end === 'number' &&
                    end > start
                  ) {
                    return acc + (end - start);
                  }
                  return acc;
                }, 0);
                if (!total || total < 1000) {
                  if (fsm.recordSamples.length > 0) return '0.0 s';
                  return 'unknown';
                }
                return (total / 1000).toFixed(2) + ' s';
              })()}
            </IonLabel>
          </IonItem>
          <IonItem lines="none" style={{ background: 'transparent' }}>
            <IonLabel>
              <Trans i18nKey="BLE_DISCONNECTED">BLE Disconnected</Trans>
            </IonLabel>
            <IonLabel slot="end">
              {(() => {
                // Zeit getrennt = Zeit zwischen Sessions
                const arr = Array.isArray(fsm.recordSessionTimestamps)
                  ? fsm.recordSessionTimestamps
                  : [];
                if (arr.length < 2) {
                  if (fsm.recordSamples.length > 0) return '0.0 s';
                  return 'unknown';
                }
                // Nur abgeschlossene Sessions
                const valid = arr.filter(
                  ([start, end]) =>
                    typeof start === 'number' &&
                    typeof end === 'number' &&
                    end > start
                );
                if (valid.length < 2) {
                  if (fsm.recordSamples.length > 0) return '0.0 s';
                  return 'unknown';
                }
                let disconnected = 0;
                for (let i = 1; i < valid.length; i++) {
                  const prevEnd = valid[i - 1][1];
                  const currStart = valid[i][0];
                  if (
                    typeof prevEnd === 'number' &&
                    typeof currStart === 'number' &&
                    currStart > prevEnd
                  ) {
                    disconnected += currStart - prevEnd;
                  }
                }
                return (disconnected / 1000).toFixed(2) + ' s';
              })()}
            </IonLabel>
          </IonItem>
          <IonItem lines="none" style={{ background: 'transparent' }}>
            <IonLabel>
              <Trans i18nKey="BLE_CONNECTED_PERCENT">BLE Connected (%)</Trans>
            </IonLabel>
            <IonLabel slot="end">
              {(() => {
                // Nur abgeschlossene Sessions
                const arr = Array.isArray(fsm.recordSessionTimestamps)
                  ? fsm.recordSessionTimestamps
                  : [];
                if (arr.length === 0) return 'unknown';
                const valid = arr.filter(
                  ([start, end]) =>
                    typeof start === 'number' &&
                    typeof end === 'number' &&
                    end > start
                );
                if (valid.length === 0) return 'unknown';
                const connected = valid.reduce(
                  (acc, [start, end]) => acc + (end! - start),
                  0
                );
                let disconnected = 0;
                for (let i = 1; i < valid.length; i++) {
                  const prevEnd = valid[i - 1][1];
                  const currStart = valid[i][0];
                  if (
                    typeof prevEnd === 'number' &&
                    typeof currStart === 'number' &&
                    currStart > prevEnd
                  ) {
                    disconnected += currStart - prevEnd;
                  }
                }
                const total = connected + disconnected;
                if (total === 0) return 'unknown';
                return ((connected / total) * 100).toFixed(2) + ' %';
              })()}
            </IonLabel>
          </IonItem>
          <IonItem lines="none" style={{ background: 'transparent' }}>
            <IonLabel>
              <Trans i18nKey="BLE_DISCONNECTED_PERCENT">
                BLE Disconnected (%)
              </Trans>
            </IonLabel>
            <IonLabel slot="end">
              {(() => {
                // Nur abgeschlossene Sessions
                const arr = Array.isArray(fsm.recordSessionTimestamps)
                  ? fsm.recordSessionTimestamps
                  : [];
                if (arr.length === 0) return 'unknown';
                const valid = arr.filter(
                  ([start, end]) =>
                    typeof start === 'number' &&
                    typeof end === 'number' &&
                    end > start
                );
                if (valid.length === 0) return 'unknown';
                const connected = valid.reduce(
                  (acc, [start, end]) => acc + (end! - start),
                  0
                );
                let disconnected = 0;
                for (let i = 1; i < valid.length; i++) {
                  const prevEnd = valid[i - 1][1];
                  const currStart = valid[i][0];
                  if (
                    typeof prevEnd === 'number' &&
                    typeof currStart === 'number' &&
                    currStart > prevEnd
                  ) {
                    disconnected += currStart - prevEnd;
                  }
                }
                const total = connected + disconnected;
                if (total === 0) return 'unknown';
                return ((disconnected / total) * 100).toFixed(2) + ' %';
              })()}
            </IonLabel>
          </IonItem>
          <IonItem lines="none" style={{ background: 'transparent' }}>
            <IonLabel>
              <Trans i18nKey="SAMPLES_RECORDED">Samples Recorded</Trans>
            </IonLabel>
            <IonLabel slot="end">{fsm.recordSamples.length}</IonLabel>
          </IonItem>
          <IonItem lines="none" style={{ background: 'transparent' }}>
            <IonLabel>
              <Trans i18nKey="CONNECTION_LOST_COUNT">Ausfälle (gesamt)</Trans>
            </IonLabel>
            <IonLabel slot="end">{fsm.connectionLostCounterValue}</IonLabel>
          </IonItem>
        </div>
        {/* Timeline-Visualisierung für Verbindung (grün=verbunden, rot=getrennt, gelb=Verbindung verloren aber Samples empfangen) als separater Block */}
        {!isWaitingForSamples && (
          <div
            style={{
              marginTop: 32,
              marginBottom: 8,
              padding: 16,
              background: 'rgba(0,0,0,0.5)',
              borderRadius: 10,
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            }}
          >
            <IonLabel
              style={{ fontWeight: 600, marginBottom: 4, display: 'block' }}
            >
              <Trans i18nKey="CONNECTION_TIMELINE">Connection Timeline</Trans>
            </IonLabel>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                height: 18,
                background: '#eee',
                borderRadius: 6,
                overflow: 'hidden',
                minWidth: 120,
                position: 'relative',
              }}
            >
              {(() => {
                // Verwende connectionTimeline direkt für die Visualisierung: Nur grün, wenn BLE verbunden war
                const timeline = Array.isArray(fsm.connectionTimeline)
                  ? fsm.connectionTimeline
                  : [];
                if (
                  !timeline ||
                  !Array.isArray(timeline) ||
                  timeline.length === 0
                ) {
                  return (
                    <span
                      style={{ fontSize: 12, color: '#888', paddingLeft: 8 }}
                    >
                      No data
                    </span>
                  );
                }
                // min/max für Prozent-Berechnung
                const starts = Array.isArray(timeline)
                  ? (timeline
                      .map((e) =>
                        e && typeof e.start === 'number' ? e.start : null
                      )
                      .filter((s) => typeof s === 'number') as number[])
                  : [];
                const ends = Array.isArray(timeline)
                  ? (timeline
                      .map((e) =>
                        e && typeof e.end === 'number' ? e.end : null
                      )
                      .filter((e) => typeof e === 'number') as number[])
                  : [];
                const min = starts.length > 0 ? Math.min(...starts) : 0;
                const max = ends.length > 0 ? Math.max(...ends) : 0;
                const total = max - min || 1;
                // Session-Grenzen als Marker (Striche) einzeichnen
                const sessionResolved = Array.isArray(
                  fsm.recordSessionTimestampsResolved
                )
                  ? fsm.recordSessionTimestampsResolved
                  : [];
                const markerColor = '#222';
                const markerStyle = (pos: number): React.CSSProperties => ({
                  position: 'absolute' as const,
                  left: ((pos - min) / total) * 100 + '%',
                  top: 0,
                  height: '100%',
                  width: 2,
                  background: markerColor,
                  zIndex: 2,
                  borderRadius: 1,
                });
                // Marker für alle Session-Starts und -Ends
                const markers: ReactElement[] = [];
                sessionResolved.forEach(([start, end], i) => {
                  if (typeof start === 'number')
                    markers.push(
                      <div
                        key={'marker-start-' + i}
                        style={markerStyle(start)}
                        title={`Session ${i + 1} Start: ${((start - min) / 1000).toFixed(2)}s`}
                      />
                    );
                  if (typeof end === 'number')
                    markers.push(
                      <div
                        key={'marker-end-' + i}
                        style={markerStyle(end)}
                        title={`Session ${i + 1} End: ${((end - min) / 1000).toFixed(2)}s`}
                      />
                    );
                });
                // Timeline-Balken
                const bars = Array.isArray(timeline)
                  ? timeline.map((e, idx) => {
                      if (
                        !e ||
                        typeof e.start !== 'number' ||
                        typeof e.end !== 'number'
                      )
                        return null;
                      let color = 'var(--ion-color-danger)';
                      if (e.type === 'connected')
                        color = 'var(--ion-color-success)';
                      else if (e.type === 'lostButReceived')
                        color = 'var(--ion-color-warning)';
                      // 'disconnected' bleibt rot
                      const width = ((e.end - e.start) / total) * 100;
                      let title = '';
                      if (color === 'var(--ion-color-success)')
                        title = `Connected: ${((e.end - e.start) / 1000).toFixed(1)}s`;
                      else if (color === 'var(--ion-color-warning)')
                        title = `Disconnected (samples): ${((e.end - e.start) / 1000).toFixed(1)}s`;
                      else
                        title = `Disconnected: ${((e.end - e.start) / 1000).toFixed(1)}s`;
                      return (
                        <div
                          key={'bleblock-' + idx}
                          style={{
                            width: width + '%',
                            height: '100%',
                            background: color,
                            display: 'inline-block',
                            zIndex: 1,
                          }}
                          title={title}
                        />
                      );
                    })
                  : null;
                return (
                  <>
                    {bars}
                    {markers}
                  </>
                );
              })()}
            </div>
            {/* Warnung bei überlappenden oder unsortierten Sessions */}
            {(() => {
              const arr = Array.isArray(fsm.recordSessionTimestampsResolved)
                ? fsm.recordSessionTimestampsResolved
                : [];
              let overlap = false;
              for (let i = 1; i < arr.length; i++) {
                if (arr[i][0] < arr[i - 1][1]) overlap = true;
              }
              if (overlap) {
                return (
                  <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>
                    <b>Achtung:</b> Sessions überschneiden sich oder sind
                    unsortiert!
                  </div>
                );
              }
              return null;
            })()}
            <div
              style={{
                fontSize: 11,
                color: 'var(--ion-color-text)',
                marginTop: 2,
              }}
            >
              <span
                style={{ color: 'var(--ion-color-success)', fontWeight: 600 }}
              >
                ■
              </span>{' '}
              <Trans i18nKey="CONNECTED">Connected</Trans> &nbsp;
              <span
                style={{ color: 'var(--ion-color-warning)', fontWeight: 600 }}
              >
                ■
              </span>{' '}
              <Trans i18nKey="LOST_BUT_RECEIVED">
                Lost but received samples
              </Trans>{' '}
              &nbsp;
              <span
                style={{ color: 'var(--ion-color-danger)', fontWeight: 600 }}
              >
                ■
              </span>{' '}
              <Trans i18nKey="DISCONNECTED">Disconnected</Trans>
            </div>
          </div>
        )}
      </IonContent>
    </IonModal>
  );
};

export default RecordInfoModal;
